import { auth, onAuthStateChanged } from "./firebaseApi";
import { APP_CONFIG } from "../constants/appConstants";

function waitForAuthUser(timeoutMs = 8000) {
  if (auth.currentUser) return Promise.resolve(auth.currentUser);
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      unsub();
      reject(new Error("Authentication not ready. Please retry."));
    }, timeoutMs);
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        clearTimeout(timer);
        unsub();
        resolve(user);
      }
    });
  });
}

async function getAuthHeader(forceRefresh = false) {
  const user = await waitForAuthUser();
  if (!user) throw new Error("Not authenticated");
  const token = await user.getIdToken(forceRefresh);
  return { Authorization: `Bearer ${token}` };
}

async function api(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    ...(await getAuthHeader(false)),
    ...(options.headers || {}),
  };
  let res = await fetch(`${APP_CONFIG.ADMIN_API_BASE_URL}${path}`, {
    ...options,
    headers,
  });
  if (res.status === 401) {
    res = await fetch(`${APP_CONFIG.ADMIN_API_BASE_URL}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(await getAuthHeader(true)),
        ...(options.headers || {}),
      },
    });
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data?.error || data?.message || "Request failed" };
  }
  return { ok: true, data };
}

export const adminService = {
  getUsers() {
    return api("/admin/users");
  },
  updateUserRole(uid, role) {
    return api(`/admin/users/${uid}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    });
  },
  updateUser(uid, payload) {
    return api(`/admin/users/${uid}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  deleteUser(uid) {
    return api(`/admin/users/${uid}`, {
      method: "DELETE",
    });
  },
  getDevices() {
    return api("/admin/devices");
  },
  reassignDevice(deviceId, ownerUid) {
    return api(`/admin/devices/${deviceId}/owner`, {
      method: "PATCH",
      body: JSON.stringify({ ownerUid }),
    });
  },
  updateDevice(deviceId, payload) {
    return api(`/admin/devices/${deviceId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },
  pushDeviceConfig(deviceId) {
    return api(`/admin/devices/${deviceId}/push-config`, { method: "POST" });
  },
  publishDeviceFirmware(deviceId, payload) {
    return api(`/admin/devices/${deviceId}/firmware`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  getDeviceConfig(deviceId) {
    return api(`/admin/devices/${deviceId}/config`);
  },
  deleteDevice(deviceId) {
    return api(`/admin/devices/${deviceId}`, {
      method: "DELETE",
    });
  },
  listDevicePool() {
    return api("/admin/device-pool");
  },
  provisionDevicePool(payload) {
    return api("/admin/device-pool", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  deleteDevicePoolId(deviceId) {
    return api(`/admin/device-pool/${encodeURIComponent(deviceId)}`, {
      method: "DELETE",
    });
  },
  getFirmwareStatus() {
    return api("/admin/firmware/status");
  },
  publishGlobalFirmware(payload) {
    return api("/admin/firmware/publish", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  listFaqs() {
    return api("/admin/faqs");
  },
  createFaq(payload) {
    return api("/admin/faqs", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  updateFaq(id, payload) {
    return api(`/admin/faqs/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
  deleteFaq(id) {
    return api(`/admin/faqs/${id}`, {
      method: "DELETE",
    });
  },
  listStories() {
    return api("/admin/stories");
  },
  createStory(payload) {
    return api("/admin/stories", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  updateStory(id, payload) {
    return api(`/admin/stories/${id}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
  deleteStory(id) {
    return api(`/admin/stories/${id}`, {
      method: "DELETE",
    });
  },
  listAuditLogs() {
    return api("/admin/audit-logs");
  },
  listServerLogs() {
    return api("/admin/server-logs");
  },
  listContentBlocks() {
    return api("/admin/content-blocks");
  },
  upsertContentBlock(key, payload) {
    return api(`/admin/content-blocks/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },

  // Home modules
  listHomeModules() {
    return api("/admin/home-modules");
  },
  createHomeModule(payload) {
    return api("/admin/home-modules", { method: "POST", body: JSON.stringify(payload) });
  },
  updateHomeModule(id, payload) {
    return api(`/admin/home-modules/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteHomeModule(id) {
    return api(`/admin/home-modules/${id}`, { method: "DELETE" });
  },

  // Pricing plans
  listPricingPlans() {
    return api("/admin/pricing-plans");
  },
  createPricingPlan(payload) {
    return api("/admin/pricing-plans", { method: "POST", body: JSON.stringify(payload) });
  },
  updatePricingPlan(id, payload) {
    return api(`/admin/pricing-plans/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deletePricingPlan(id) {
    return api(`/admin/pricing-plans/${id}`, { method: "DELETE" });
  },

  // Future items + meta
  getFutureItemsAdmin() {
    return api("/admin/future-items");
  },
  createFutureItem(payload) {
    return api("/admin/future-items", { method: "POST", body: JSON.stringify(payload) });
  },
  updateFutureItem(id, payload) {
    return api(`/admin/future-items/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteFutureItem(id) {
    return api(`/admin/future-items/${id}`, { method: "DELETE" });
  },
  updateFutureMeta(payload) {
    return api("/admin/future-meta", { method: "PUT", body: JSON.stringify(payload) });
  },

  // Contact features/channels + responses
  listContactFeaturesAdmin() {
    return api("/admin/contact-features");
  },
  createContactFeature(payload) {
    return api("/admin/contact-features", { method: "POST", body: JSON.stringify(payload) });
  },
  updateContactFeature(id, payload) {
    return api(`/admin/contact-features/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteContactFeature(id) {
    return api(`/admin/contact-features/${id}`, { method: "DELETE" });
  },

  listContactChannelsAdmin() {
    return api("/admin/contact-channels");
  },
  createContactChannel(payload) {
    return api("/admin/contact-channels", { method: "POST", body: JSON.stringify(payload) });
  },
  updateContactChannel(id, payload) {
    return api(`/admin/contact-channels/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteContactChannel(id) {
    return api(`/admin/contact-channels/${id}`, { method: "DELETE" });
  },

  listContactResponsesAdmin() {
    return api("/admin/contact-responses");
  },
  updateContactResponse(id, payload) {
    return api(`/admin/contact-responses/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  deleteContactResponse(id) {
    return api(`/admin/contact-responses/${id}`, { method: "DELETE" });
  },

  listAlertRulesAdmin() {
    return api("/admin/alert-rules");
  },
  createAlertRule(payload) {
    return api("/admin/alert-rules", { method: "POST", body: JSON.stringify(payload) });
  },
  updateAlertRule(id, payload) {
    return api(`/admin/alert-rules/${id}`, { method: "PUT", body: JSON.stringify(payload) });
  },
  deleteAlertRule(id) {
    return api(`/admin/alert-rules/${id}`, { method: "DELETE" });
  },

  getNotificationStats() {
    return api("/admin/notifications/stats");
  },
  sendTestNotification(payload) {
    return api("/admin/notifications/test", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },
  broadcastNotification(payload) {
    return api("/admin/notifications/broadcast", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async uploadImage(file) {
    if (!file) throw new Error("file is required");
    const tokenHeader = await getAuthHeader();
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${APP_CONFIG.ADMIN_API_BASE_URL}/admin/upload`, {
      method: "POST",
      headers: tokenHeader,
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: data?.error || data?.message || "Upload failed" };
    }
    return { ok: true, data };
  },
  uploadFirmware(file) {
    return this.uploadImage(file);
  },
};

